import React, { useContext } from 'react';
import styled from 'styled-components';
import { usePostHog } from 'posthog-js/react';
import { ThemeContext } from '../ThemeContext';

const Container = styled.div`
  padding: 20px;
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 1px solid ${props => props.theme === 'light' ? '#eee' : '#333'};
`;

const ProfilePic = styled.img`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  margin-right: 20px;
  object-fit: cover;
`;

const HeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const Name = styled.h2`
  font-size: 1.8rem;
  font-weight: bold;
  margin: 0 0 5px 0;
  color: ${props => props.theme === 'light' ? '#333' : '#e0e0e0'};
`;

const Subtitle = styled.p`
  font-size: 1rem;
  color: ${props => props.theme === 'light' ? '#666' : '#b0b0b0'};
  margin: 0;

  a {
    color: var(--highlight-color-${props => props.theme === 'light' ? 'light' : 'dark'});
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const Section = styled.div`
  margin-bottom: 30px;
`;

const SectionTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 15px;
  color: ${props => props.theme === 'light' ? '#555' : '#c0c0c0'};
`;

const Paragraph = styled.p`
  margin-bottom: 15px;
  line-height: 1.6;
  color: ${props => props.theme === 'light' ? '#555' : '#b0b0b0'};

  strong {
    color: var(--highlight-color-${props => props.theme === 'light' ? 'light' : 'dark'});
  }
`;

const List = styled.ul`
  list-style: disc;
  padding-left: 20px;
  margin: 0 0 15px 0;
`;

const ListItem = styled.li`
  margin-bottom: 8px;
  line-height: 1.6;
  color: ${props => props.theme === 'light' ? '#555' : '#b0b0b0'};

  strong {
    color: var(--highlight-color-${props => props.theme === 'light' ? 'light' : 'dark'});
  }

  a {
    color: var(--highlight-color-${props => props.theme === 'light' ? 'light' : 'dark'});
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const AboutSection = () => {
  const { theme } = useContext(ThemeContext);
  const posthog = usePostHog();

  const trackOutbound = (label, url) => () => {
    posthog?.capture('outbound_link_clicked', { source: 'about', label, url });
  };

  return (
    <Container>
      <Header theme={theme}>
        <ProfilePic src="/images/portrait.webp" alt="Profile Portrait" />
        <HeaderInfo>
          <Name theme={theme}>Xander Jennie</Name>
          <Subtitle theme={theme}>Product Manager currently working at <a href="https://openup.com/" target="_blank" rel="noopener noreferrer" onClick={trackOutbound('OpenUp', 'https://openup.com/')}>OpenUp</a></Subtitle>
        </HeaderInfo>
      </Header>

      <Section>
        <Paragraph theme={theme}>
          Hi! I'm Xander, a Product Manager and hobby developer. I...
        </Paragraph>
        <List>
          <ListItem theme={theme}>Am an <strong>experienced product manager</strong> working on B2B and B2C Digital Products,</ListItem>
          <ListItem theme={theme}>Create web applications and browser extensions to <strong>solve problems</strong> that I can't believe haven't been solved yet,</ListItem>
          <ListItem theme={theme}>Write <strong>articles</strong> about concepts that intrigue me</ListItem>
        </List>
      </Section>

      <Section>
        <SectionTitle theme={theme}>Language Proficiency</SectionTitle>
        <Paragraph theme={theme}>
          I have native fluency in both <strong>English</strong> and <strong>Dutch</strong>, and can speak conversational <strong>Spanish</strong>.
        </Paragraph>
        <Paragraph theme={theme}>
        As I am from <a href='https://www.google.com/search?q=curacao' target="_blank" rel="noopener noreferrer" onClick={trackOutbound('Curacao', 'https://www.google.com/search?q=curacao')}>Curaçao</a>, I also speak <strong>Papiamentu</strong> as on a native level.
        </Paragraph>
      </Section>
      
      <Section>
        <SectionTitle theme={theme}>Education</SectionTitle>
        <Paragraph theme={theme}>
          Bachelor of Science in Information & Communication Technology (ICT) {/*TODO: Add my minor*/}
          <br />
          <span style={{ fontSize: '0.9em', color: theme === 'light' ? '#888' : '#999' }}>(GRADUATED CUM LAUDE)</span>
        </Paragraph>
      </Section>
      
      <Section>
        <SectionTitle theme={theme}>Other Interests</SectionTitle>
        <List>
          <ListItem theme={theme}>Traveling - as I'm a naturally curious person</ListItem>
          <ListItem theme={theme}>Exploring new technologies</ListItem>
          <ListItem theme={theme}>Coding Side Projects</ListItem>
        </List>
      </Section>

    </Container>
  );
};

export default AboutSection; 